import React from 'react';
import { withRouter } from 'react-router';
import { Paper, Grid, TextField, Checkbox, Button } from '@material-ui/core';
import { flatten, omit, pathOr, clone, pick, mergeWith } from 'ramda';
import { withResource, resourceTypes, gettersOf } from 'dataModule/store/resources';
import { withNamespaces } from 'react-i18next';
import DM from 'dataModule';
import { merge, path } from 'ramda';
import SignaturePad from 'appModule/signature/containers/signaturePad';
import { isEqual, isEmpty } from 'lodash';
import { fetchGoThumbSignedUrl } from 'utilsModule/gothumb';
import moment from 'moment';

const style = {
  sticky: {
    position: 'sticky',
    bottom: 0,
    background: 'inherit',
    zIndex: 9,
    paddingBottom: 15,
  },
  signatureBox: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
    background: '#ffffff',
    border: '1px solid #c9c9c9',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'border-color 0.2s ease',
  },
  signaturePlaceholder: {
    color: '#757575',
    fontSize: '13px',
    fontWeight: 500,
    letterSpacing: '0.2px',
    pointerEvents: 'none',
    userSelect: 'none',
    textAlign: 'center',
  },
  docHeader: {
    width: '100%',
    background: '#ffffff',
    overflow: 'hidden',
  },
  docHeaderBody: {
    minHeight: '118px',
    padding: '18px 48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '24px',
    boxSizing: 'border-box',
  },
  docHeaderCopy: {
    flex: '1 1 0',
    minWidth: 0,
    textAlign: 'center',
  },
  docHeaderCompany: {
    margin: '0 0 8px',
    color: '#4f4f4f',
    fontSize: '13px',
    fontWeight: 600,
    lineHeight: 1.35,
    letterSpacing: '0.2px',
  },
  docHeaderAddress: {
    margin: 0,
    color: '#666666',
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: 1.35,
    letterSpacing: '0.2px',
  },
  docHeaderContact: {
    margin: '8px 0 0',
    color: '#7b7b7b',
    fontSize: '11px',
    fontWeight: 500,
    lineHeight: 1.35,
    letterSpacing: '0.2px',
  },
  docHeaderLogoWrap: {
    flex: '0 0 auto',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '190px',
    paddingRight: '18px',
  },
  docHeaderLogoCircle: {
    width: '108px',
    height: '108px',
    borderRadius: '50%',
    overflow: 'hidden',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#ffffff',
  },
  docHeaderLogo: {
    display: 'block',
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
}

const normalizeLanguageCode = (language) => String(language || 'en').toLowerCase().replace('_', '-').split('-')[0];

const momentLocaleMap = {
  en: 'en',
  vi: 'vi',
  zh: 'zh-cn',
  th: 'th',
  id: 'id',
  ms: 'ms',
};

const getMomentLocale = (language) => momentLocaleMap[normalizeLanguageCode(language)] || momentLocaleMap.en;

export const formatLocalizedDate = (dateValue, language = 'en') => {
  if (!dateValue) return null;
  const parsedDate = moment(dateValue);
  if (!parsedDate.isValid()) return null;

  return parsedDate.locale(getMomentLocale(language)).format('ll');
};

const formatProgramDate = (dateValue, language = 'en') => {
  if (!dateValue) return null;
  const parsedDate = moment(dateValue);
  return parsedDate.isValid()
    ? parsedDate.locale(getMomentLocale(language)).format('ll')
    : null;
};

const normalizeDurationCalendar = (calendar) => {
  if (!calendar) return null;
  if (typeof calendar === 'string') return calendar;
  return pathOr(null, ['value'], calendar);
};

const formatProgramDuration = (duration = {}) => {
  const length = pathOr(null, ['length'], duration);
  const calendar = normalizeDurationCalendar(pathOr(null, ['calendar'], duration));

  if (length === null || length === undefined || !calendar) return null;

  const normalizedCalendar = `${calendar}`.toUpperCase();
  const durationLabels = {
    DAY: 'day',
    WEEK: 'week',
    MONTH: 'month',
    YEAR: 'year',
  };

  const unit = durationLabels[normalizedCalendar] || normalizedCalendar.toLowerCase();
  const numericLength = Number(length);
  const usePlural = Number.isFinite(numericLength) ? numericLength !== 1 : true;

  return `${length} ${unit}${usePlural ? 's' : ''}`;
};

export const runtimeProps = {
  syncHtml: (html, toSync) => {
    const _syncHtml = (_html, repeatData = null) => {
      return flatten(clone(_html).map((e) => {
        if (e.hasOwnProperty('toCheck')) {
          if (toSync[e.toCheck] === false) {
            return [];
          }
        }

        if (e.isRepeat && toSync[e.dataKey] && repeatData === null) {
          const b = flatten(toSync[e.dataKey].map((j, _i) => {
            return _syncHtml([e], { ...j, index: _i, rowNumber: _i + 1 });
          }))
          return b;
        } else if (repeatData && e.valuePath) {
          e.value = pathOr(null, e.valuePath, repeatData);
        } else if (repeatData && e.useRepeatedIndex) {
          e.index = pathOr(null, ['index'], repeatData);
        } else if (e.childrens) {
          let c = false;
          if (e.childrens.some(g => g.isRepeat)) c = true;
          e.childrens = flatten(_syncHtml(e.childrens, repeatData))
        } else {

          if (e.syncValue) {
            if (e.id && toSync[e.id]) {
              if (e.tag === 'img') {
                e.props.src = toSync[e.id]
              } else {
                e.value = toSync[e.id]
              }
            } else if (e.translationKey && e.translationParams) {
              for (const key in e.translationParams) {
                if (toSync[e.translationParams[key]]) {
                  const v = toSync[e.translationParams[key]]
                  e.translationParams[key] = v;
                }
              }
            }
          }
          if (e.valuePath) {
            e.value = pathOr(null, e.valuePath, toSync);
          }
        }

        return e;
      }))
    }
    const result = _syncHtml(html);
    return result;
  }
}

export const getNestedValue = (obj, pathString) => {
  if (!obj || !pathString) return null;
  return pathString.split('.').reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : null), obj);
};

export const normalizeImageSrc = (value, fallbackMimeType = 'image/png') => {
  // Normalizes image values used by header/footer templates. Existing URLs, local paths,
  // blob URLs, and full data URIs are returned unchanged. If a template provides only a
  // raw base64 payload, we detect it and wrap it in a data URI so the browser can render it.
  if (!value) return value;

  const src = typeof value === 'object' ? value.src : value;
  const mimeType = typeof value === 'object'
    ? (value.mimeType || value.contentType || value.type || value.mime || fallbackMimeType)
    : fallbackMimeType;

  if (typeof src !== 'string') return src;

  const trimmedSrc = src.trim();
  if (!trimmedSrc) return trimmedSrc;

  // Leave normal image sources alone: URLs, data URIs, blob URLs, and local paths.
  if (
    /^data:/i.test(trimmedSrc)
    || /^blob:/i.test(trimmedSrc)
    || /^(https?:)?\/\//i.test(trimmedSrc)
    || trimmedSrc.startsWith('/')
    || trimmedSrc.startsWith('./')
    || trimmedSrc.startsWith('../')
  ) {
    return trimmedSrc;
  }

  // Some templates store only the raw base64 payload, so wrap it as a data URI for the browser.
  const compactSrc = trimmedSrc.replace(/\s+/g, '');
  const isBase64Payload = compactSrc.length >= 32 && /^[A-Za-z0-9+/]+=*$/.test(compactSrc);

  if (!isBase64Payload) return trimmedSrc;

  return `data:${mimeType};base64,${compactSrc}`;
};

export const buildOrganizationAddressParts = (organization = {}) => {
  const address = Array.isArray(organization.address) ? organization.address[0] || {} : {};
  const line1 = address.line_1 || address.line1 || '';
  const line2Parts = [
    address.line_2 || address.line2 || '',
    address.city || '',
    address.state || '',
    address.postalCode || '',
    address.country || '',
  ].filter(Boolean);
  const line2 = line2Parts.join(', ');

  return {
    line1,
    line2,
    full: [line1, line2].filter(Boolean).join(', '),
  };
};

@withResource([
  { resourceType: resourceTypes.PROGRAMS, method: 'retrieveAP' },
  { resourceType: resourceTypes.PROGRAMS, method: 'retrievePP' },
  {
    resourceType: resourceTypes.PATIENTS,
    method: 'retrievePatient',
    input: ({
      match: {
        params: { patientId },
      },
    }) => ({ params: { ids: [{ name: 'patientId', value: patientId }] } }),
    options: { runOnDidMount: true },
  },
])
@withRouter
@withNamespaces('modules', { wait: true })
export default class ConsentForm extends React.Component {
  constructor(props) {
    super(props)

    let formData = props.formData || {};
    for (let item in formData) {
      if (isEmpty(formData[item])) {
        formData[item] = null;
      }
    }

    this.state = {
      loading: true,
      openSignaturePad: false,
      target_signature_id: null,
      awaitPerformAction: false,
      disableFinishButton: false,
      clinicLogoUrl: null,
      formTemplate: null,
      formData: {
        ...formData,
      },
      hasDevice: true,
    };
  }

  async componentDidMount() {
    const {
      provider: { organization },
    } = this.props;
    const logo = await fetchGoThumbSignedUrl(organization.logoUrl);

    await this.setState({ clinicLogoUrl: logo, });
    await this._loadHtml()
    await this.setState({ loading: false, isMounted: true });
  }

  fetchSkus = (value) => {
    const {
      retrieveAP: { vitals } = { vitals: [] },
      retrieveVitals = [],
      enrollPP,
    } = this.props;

    const skus = [];

    const skuIds = [];
    for (const vital of vitals) {
      skuIds.push(vital.skuId);
    }

    retrieveVitals.forEach((e, i) => {
      const findSku = e.skus.find((sku) => skuIds.includes(sku.id));
      if (findSku) {
        if (!skus.find((x) => x.id === findSku.id)) {
          let findSKUSerialNumber = '';
          if (findSku.extraInfo !== 'BYOD') {
            findSKUSerialNumber = Object.entries(enrollPP.vitals).find(([, vitalEntry]) => vitalEntry.sku.value === findSku.id)[1].device.value;
          }
          skus.push({ ...findSku, vitalName: e.label, serialNumber: findSKUSerialNumber });
        }
      }
    });

    const _skus = skus;
    const countDevice = _skus.filter((e) => e.extraInfo !== 'BYOD').length;
    const hasDevice = countDevice > 0;

    return {
      skus: _skus,
      hasDevice,
      ...hasDevice > 0 ? {
        devices: _skus.map((e) => ({ check: false, vitalName: e.vitalName, label: e.label, name: e.name, type: e.type, id: e.id, model: e.model, serialNumber: e.serialNumber })),
        deviceCheck: (this.state.formData.deviceCheck) ? this.state.formData.deviceCheck : Array(countDevice).fill(false),
        equipmentCheck: (!this.state.formData.equipmentCheck) ? value.initialValue.equipmentCheck : this.state.formData.equipmentCheck,
      } : {}
    }
  };

  shouldComponentUpdate = (nextProps, nextState) => {
    if (!isEqual(nextProps.formData, nextProps.formData)) {
      return false
    }
    return true
  };

  _loadHtml = async () => {
    const {
      provider: { profile = {}, organization = {} } = {},
      data: { patients: { retrievePatient: { patient } } },
      i18n: { language },
      type
    } = this.props;

    const {
      formData: { effectiveDate },
    } = this.state;
    const devices = this.state.formData.devices;

    let { value } = await DM.program.provider.getEnrollmentFormTemplate({
      orgId: organization.id,
      formType: type,
      language: language,
    });

    let patientName = null;
    let clinicianName = null;
    let _patient = null
    if (patient && patient.currentDemographics && patient.currentDemographics.name) {
      patientName = `${patient.currentDemographics.name.firstName} ${patient && patient.currentDemographics.name.lastName}`
      _patient = patient.currentDemographics;
    }
    if (profile && profile.name) {
      clinicianName = `${profile.name.firstName} ${profile.name.lastName}`
    }

    let skus = null;

    if (value) skus = await this.fetchSkus(value);

    const allNullExceptType = obj =>
      Object.entries(obj)
        .filter(([k]) => k !== 'type')
        .every(([, v]) => v === null);

    const {
      hasDevice
    } = skus;

    const updatedData = {
      ...!allNullExceptType(this.state.formData) ? this.state.formData : (!isEmpty(value) && !isEmpty(value.initialValue)) ? value.initialValue : this.state.formData,
      ...skus,
      ...this._buildProgramDetails(),
      clinicianName: clinicianName,
      patientName: patientName,
      patient: _patient ? pick(['name', 'birthDate', 'identification', 'mobile', 'height', 'weight', 'address', 'nationality', 'race', 'gender'], _patient) : null,
      effectiveDate: effectiveDate || new Date().toISOString()
    }

    if (value && value.html) {
      const data = { ...updatedData, clinicLogoUrl: this.state.clinicLogoUrl, clinicName: organization.name, effectiveDate: effectiveDate || new Date().toISOString() }
      const _html = runtimeProps.syncHtml(value.html, data);
      try {
        value.html = _html
      } catch (e) {
        console.log(e)
      }
    }

    if (value && value.documentConfig) {
      value.documentConfig = {
        ...value.documentConfig,
        data: this._buildDocumentConfigData(value.documentConfig, organization),
      };
    }


    this.setState(prevState => ({
      formTemplate: value,
      formData: {
        ...updatedData
      },
      hasDevice
    }), () => {
      console.log(this.state);
    })

  }

  renderFooter = () => {
    const { disableFinishButton } = this.state;
    const { isFinish, t, history, buttons = { back: t('common:buttonLabels.back'), skip: t('common:buttonLabels.next') }, isSubmitting = false, location: { pathname } } = this.props;
    const { back, skip } = buttons;

    let proceedButton = skip;
    let callback = () => this._saveFormData(() => this.props._skipPDF());
    let disable = (isSubmitting || !this._isFormComplete()) && pathname.includes('/careplans/enrol');
    if (isFinish) {
      proceedButton = t('common:buttonLabels.finish');
      callback = () => this._handleFinish();
      disable = disableFinishButton || !this._isFormComplete();
    }

    return (<Grid
      container
      style={merge(style.sticky, { paddingTop: '10px', paddingBottom: '10px', backgroundColor: '#FFFFFF' })}
      justify="center"
    >
      <Grid item xs={3} style={{ textAlign: 'left' }}>
        <Button
          className="milo-btn-white"
          variant="contained"
          style={{ width: '50%' }}
          onClick={this.props._renderBack}
        >
          {back}
        </Button>
      </Grid>
      <Grid item xs={3} style={{ textAlign: 'right' }}>
        <Button
          className="milo-btn-blue"
          disabled={disable}
          variant="contained"
          style={{ width: '50%' }}
          onClick={callback}
        >
          {proceedButton}
        </Button>
      </Grid>
    </Grid>);
  };

  _handleFormChange(el, e) {
    const { updateFormData } = this.props;
    let updateData = this.state.formData;

    let _value = null;
    if (e.props.type === 'checkbox') {
      _value = this.state.formData[e.props.name]
      _value[e.index] = el.target.checked;
    } else if (e.props.type === 'text') {
      _value = el.target.value;
    }

    updateData[e.props.name] = _value;

    this.setState(prevState => ({
      formData: {
        ...prevState.formData,
        ...updateData
      }
    }))


    if (updateFormData) {
      updateFormData({ type: 'consent', data: updateData });
    }
  }

  _buildDocumentConfigData = (documentConfig = {}, organization = {}) => {
    const baseData = pathOr({}, ['data'], documentConfig);
    const headerData = pathOr({}, ['header'], baseData);
    const address = buildOrganizationAddressParts(organization);
    const hotlineNumber = headerData.hotlineNumber || '';
    const whatsappNumber = headerData.whatsappNumber || '';
    const website = headerData.website || '';
    const emailAddress = headerData.emailAddress || '';
    const contactItems = [
      hotlineNumber ? `Hotline: ${hotlineNumber}` : null,
      whatsappNumber ? `WhatsApp: ${whatsappNumber}` : null,
      website ? `Website: ${website}` : null,
      emailAddress ? `Email: ${emailAddress}` : null,
    ].filter(Boolean);

    return {
      ...baseData,
      header: {
        ...headerData,
        organizationName: organization.name || '',
        organizationRegNo: organization.regNo || '',
        organizationDisplayName: organization.name
          ? `${organization.name}${organization.regNo ? ` (${organization.regNo})` : ''}`
          : '',
        organizationLogo: this.state.clinicLogoUrl || '',
        organizationLogoStyle: this.state.clinicLogoUrl ? '' : 'display:none;',
        organizationAddressLine1: address.line1,
        organizationAddressLine2: address.line2,
        organizationAddress: address.full,
        organizationAddressStyle: address.full ? '' : 'display:none;',
        hotlineNumber,
        whatsappNumber,
        website,
        emailAddress,
        contactInfoLine: contactItems.join(' | '),
        contactInfoStyle: contactItems.length ? '' : 'display:none;',
      },
      footer: {
        ...pathOr({}, ['footer'], baseData),
      },
    };
  }

  _buildProgramDetails = () => {
    const { enrollPP = {}, retrieveAP = {} } = this.props;
    const info = pathOr({}, ['info'], enrollPP);
    const language = pathOr('en', ['i18n', 'language'], this.props);

    const programName = pathOr('', ['title'], info) || pathOr('', ['title'], retrieveAP) || pathOr('', ['name'], retrieveAP);
    const programStartDate = formatProgramDate(pathOr(null, ['period', 'start'], info), language);
    const programEndDate = formatProgramDate(pathOr(null, ['period', 'end'], info), language);
    const duration = pathOr({}, ['duration'], info);
    const fallbackDuration = pathOr({}, ['duration'], retrieveAP);
    const programDuration = formatProgramDuration(
      Object.keys(duration || {}).length > 0 ? duration : fallbackDuration,
    );

    return {
      programDetails: {
        epId: pathOr(null, ['id'], retrieveAP),
        programName: programName || null,
        programStartDate,
        programEndDate,
        programDuration,
      },
    };
  }

  _handleSignaturePad = (status = false, id = null, currentSrc) => {
    const { updateFormData } = this.props;
    let updateData = this.state.formData;

    if (status) {
      this.setState({ openSignaturePad: status, target_signature_id: id })
    }
    if (id && currentSrc) {
      updateData[id] = currentSrc;
      this.setState(prevState => ({
        formData: {
          ...prevState.formData,
          ...updateData
        }
      }))
    }
    this.setState({ openSignaturePad: status })


    if (!status && updateFormData) {
      updateFormData({ type: 'consent', data: updateData });
    }
  }

  _renderSignatureField = (e, formData) => {
    const signatureSrc = formData[e.srcKey];
    const imageStyle = pathOr({}, ['props', 'style'], e);
    const wrapperStyle = {
      ...style.signatureBox,
      ...imageStyle,
    };
    const signedImageStyle = {
      display: 'block',
      width: '100%',
      height: '100%',
      objectFit: 'contain',
      ...imageStyle,
    };

    return (
      <div
        id={e.srcKey}
        style={wrapperStyle}
        onClick={() => this._handleSignaturePad(true, e.srcKey)}
        title={this.props.t('common:signaturePad.clickToSignTitle')}
      >
        {signatureSrc ? (
          <img
            {...e.props}
            src={signatureSrc}
            style={signedImageStyle}
          />
        ) : (
          <span style={style.signaturePlaceholder}>{this.props.t('common:signaturePad.clickHereToSign')}</span>
        )}
      </div>
    );
  }


  _renderParsedHtml() {
    const { t, isReadOnly } = this.props;
    const { formData, formTemplate } = this.state
    const language = pathOr('en', ['i18n', 'language'], this.props);
    if (formTemplate && formTemplate.html) {
      const { html } = formTemplate;
      const render = (item) => {
        return flatten(item.map((e) => {
          e.renderChildrens = null
          let result = null;
          if (e.childrens) {
            e.renderChildrens = render(e.childrens)
          }

          if (e.tag) {
            if (e.renderChildrens) {
              result = React.createElement(e.tag, { ...e.props }, null, e.renderChildrens)
            } else {
              let isSkipped = false;
              let element = null;
              if (e.tag === 'input' && formData.hasOwnProperty(e.props.name)) {
                if (e.props.type === 'checkbox') {
                  e.props['checked'] = formData[e.props.name][e.index];
                  if (e.props.name === 'deviceCheck') {
                    console.log(e);
                  }
                  e.props['onChange'] = (el) => this._handleFormChange(el, e);
                  element = <Checkbox {...(e.props)} />
                } else if (e.props.type === 'text') {
                  isSkipped = isReadOnly === true;
                  e.props['value'] = `${formData[e.props.name]}`;
                  e.props['onChange'] = (el) => this._handleFormChange(el, e);
                  element = <TextField {...(e.props)} value={formData[e.props.name]} />
                }
              } else if (e.tag === 'img' && e.srcKey) {
                e.props['src'] = formData[e.srcKey];
                if (e.useSignaturePad) {
                  element = this._renderSignatureField(e, formData)
                }
              }

              result = (!isSkipped) ? (element) ? element : React.createElement(e.tag, { ...e.props }, null) : null;
            }
          } else if (e.translationKey) {
            result = (t(e.translationKey, e.translationParams || {}))
          } else {
            if (e.valueKey && !e.isReadOnly) {
              result = (formData[e.valueKey])
            } else if (e.valueType === 'date') {
              result = formatLocalizedDate(e.value, language)
            } else result = (e.value)
          }

          if (e.enableHtml) {
            result = (<div dangerouslySetInnerHTML={{ __html: result }} />)
          }

          return result;
        }))
      }
      const c = render(html)
      return c;
    }
    return;
  }

  _getDocImageConfig = () => {
    const { formTemplate } = this.state;
    const headerImage = pathOr(null, ['documentConfig', 'data', 'header', 'image'], formTemplate);
    const footerImage = pathOr(null, ['documentConfig', 'data', 'footer', 'image'], formTemplate);
    const headerSrc = normalizeImageSrc(headerImage);
    const footerSrc = normalizeImageSrc(footerImage);
    const headerTemplate = pathOr(null, ['documentConfig', 'headerTemplate'], formTemplate);
    const footerTemplate = pathOr(null, ['documentConfig', 'footerTemplate'], formTemplate);
    const data = pathOr({}, ['documentConfig', 'data'], formTemplate);

    return {
      headerSrc,
      footerSrc,
      headerTemplate,
      footerTemplate,
      data,
    };
  }

  _renderDocTemplate = (template, replacements = {}) => {
    if (!template || typeof template !== 'string') return null;

    return template.replace(/<%=([\w.]+)%>/g, (match, pathString) => {
      const value = getNestedValue(replacements, pathString.trim());
      if (value && typeof value === 'object' && value.src) return normalizeImageSrc(value);
      if (/(\.|^)(src|logo)$/i.test(pathString.trim())) return normalizeImageSrc(value);
      return value !== null && value !== undefined ? `${value}` : '';
    });
  }

  _renderDocHeaderFooter = (position) => {
    const { headerSrc, footerSrc, headerTemplate, footerTemplate, data } = this._getDocImageConfig();
    const src = position === 'header' ? headerSrc : footerSrc;
    const template = position === 'header' ? headerTemplate : footerTemplate;
    const isFallbackTemplate = pathOr(false, ['isFallback'], this.state.formTemplate);

    if (position === 'header') {
      if (isFallbackTemplate) {
        return null;
      }

      if (template && src) {
        const html = this._renderDocTemplate(template, data);
        if (html) {
          return <div dangerouslySetInnerHTML={{ __html: html }} />;
        }
      }

      const headerData = pathOr({}, ['header'], data);
      const companyLine = headerData.organizationDisplayName || '';
      const address = headerData.organizationAddress || '';
      const contactInfo = headerData.contactInfoLine || '';
      const logo = normalizeImageSrc(headerData.organizationLogo || this.state.clinicLogoUrl);

      if (!companyLine && !address && !contactInfo && !logo) return null;

      return (
        <section style={style.docHeader} role="banner" aria-label="Form header">
          <div style={style.docHeaderBody}>
            <div style={style.docHeaderCopy}>
              {companyLine ? <p style={style.docHeaderCompany}>{companyLine}</p> : null}
              {address ? <p style={style.docHeaderAddress}>{address}</p> : null}
              {contactInfo ? <p style={style.docHeaderContact}>{contactInfo}</p> : null}
            </div>
            {logo ? (
              <div style={style.docHeaderLogoWrap}>
                <div style={style.docHeaderLogoCircle}>
                  <img
                    src={logo}
                    alt={headerData.organizationName || 'Organization logo'}
                    style={style.docHeaderLogo}
                  />
                </div>
              </div>
            ) : null}
          </div>
        </section>
      );
    }

    const html = this._renderDocTemplate(template, data);

    if (html) {
      return <div dangerouslySetInnerHTML={{ __html: html }} />;
    }

    if (!src) return null;

    return (
      <img
        src={src}
        alt={`template-${position}`}
        style={{
          display: 'block',
          width: '100%',
          height: 'auto',
          margin: 0,
          verticalAlign: 'top',
        }}
      />
    );
  }


  _isFormComplete = () => {
    const { formTemplate, formData, isMounted, hasDevice } = this.state;
    const initCheck = [isMounted];

    // if (formData.equipmentCheck && hasDevice) {
    //   initCheck.push(formData.equipmentCheck.checks.every((e) => e));
    //   if (formData.equipmentCheck.devices) {
    //     initCheck.push(formData.equipmentCheck.devices.every((e) => e.check));
    //   }
    // }

    if (formTemplate && formTemplate.rules) {
      const rules = formTemplate.rules;

      const getRule = (rule, expectation = null) => {
        if (rule.items) {
          let resultItems = true;
          if (rule.operator === 'and') {
            resultItems = rule.items.every((e, i) => {
              return getRule(e, rule.expectation)
            });
          } else if (rule.operator === 'or') {
            resultItems = rule.items.some((e, i) => {
              return getRule(e, rule.expectation)
            })
          }
          return resultItems;
        }

        if (rule.name && formData.hasOwnProperty(rule.name)) {
          const checks = []


          if (!hasDevice && rule.hasOwnProperty('hasDevice') && rule.hasDevice === true) {
            return checks;
          }

          if (rule.areAllTrue) {
            checks.push((formData[rule.name].every(c => c)));
          }

          if (rule.isRequired) {
            checks.push(!isEmpty(formData[rule.name]))
          }

          if (rule.isEqual) {
            checks.push(isEqual(formData[rule.name], rule.value))
          }

          if (rule.hasOwnProperty('minLength') && Number.isInteger(Number(rule.minLength))) {
            const length = formData[rule.name] ? formData[rule.name].length : 0;
            checks.push(length > Number(rule.minLength))
          }

          const result = checks.every((e) => e)
          if (expectation !== null) {
            return result === expectation
          }
          return result;
        }

        return true;
      }

      const result = getRule(rules);
      initCheck.push(result);
    }
    console.log(initCheck, initCheck.every(e => e))
    return initCheck.every((e) => e);
  }

  _handleFinish = () => {
    sessionStorage.removeItem('enrolledProgram');
    this.setState({ disableFinishButton: true });
    this._saveFormData(() => this.props._skipPDF());
  };

  _saveFormData = (callback) => {
    const { updateFormData, type, isFinish } = this.props;
    const { formData } = this.state;
    let data = formData;
    if (updateFormData) {
      updateFormData({ type: 'consent', data });
    }
    if (callback) callback();
  };

  render() {
    const { } = this.props;
    const { loading, openSignaturePad, target_signature_id } = this.state;
    return (
      <React.Fragment>
        {loading ?
          <div style={{ paddingTop: '250px' }}>
            <div className="sk-rotating-plane" />
          </div>
          :
          <React.Fragment>
            <Grid container justify="center" style={{ padding: '100px 100px 20px 100px' }}>
              <SignaturePad open={openSignaturePad} close={(id, currentSrc) => this._handleSignaturePad(false, id, currentSrc)} target_signature_id={target_signature_id} />
              <Grid item xs={7} >
                <Paper style={{ boxShadow: 'unset', overflow: 'hidden' }}>
                  <Grid container justify='center' style={{
                    padding: 0,
                  }}>
                    <Grid item xs={12}>
                      {this._renderDocHeaderFooter('header')}
                      <div style={{ padding: '25px 40px' }}>
                        {this._renderParsedHtml()}
                      </div>
                      {this._renderDocHeaderFooter('footer')}
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
            {this.renderFooter()}
          </React.Fragment>
        }
      </React.Fragment>
    );
  }
}
